// Setup-screen state: a draft match setup built on the ruleset contract in
// @aod/protocol (packages/protocol/src/ruleset.ts). Everything here is pure so
// the screen, the share code and the tests share one set of rules.
//
// A draft remembers the preset it is `basedOn`, so any change shows
// "Custom (based on X)" and Reset puts X back. `ruleset.preset` follows the
// contract: the preset id while the ruleset matches it, 'custom' once the
// ruleset differs. Assists and the AI profile are per seat, so changing only
// those keeps `ruleset.preset` but still marks the draft as customised.

import {
  type AiProfile,
  type Assists,
  type ContentRating,
  DEFAULT_YEAR_MINUTES,
  MODULE_IDS,
  type MatchSetup,
  type ModuleId,
  PRESETS,
  type PresetDef,
  type PresetId,
  type Ruleset,
  type Sea,
  type Seat,
  type SeatAlignment,
  type Thirst,
  type Tuning,
  applyContentRating,
  dependents,
  presetById,
  withDependencies,
} from '@aod/protocol';

export type PresetDefId = Exclude<PresetId, 'custom'>;

export const PRESET_IDS: readonly PresetDefId[] = PRESETS.map((p) => p.id);

export interface SetupDraft {
  /** The preset this draft started from (Reset returns here). */
  readonly basedOn: PresetDefId;
  readonly ruleset: Ruleset;
  /** Assists for seat 0, the local human. */
  readonly humanAssists: Assists;
  /** Profile shared by every AI seat. */
  readonly aiProfile: AiProfile;
  /** Seat 0's alignment choice; null = unaligned (cities start unspecialized). */
  readonly alignment: SeatAlignment | null;
  /** An authored `aod-map` document (JSON) to play on; null = procedural. */
  readonly authoredMapJson: string | null;
}

/** Modules in canonical (MODULE_IDS) order, the order presets use. */
export function sortModules(mods: Iterable<ModuleId>): ModuleId[] {
  return [...new Set(mods)].sort((a, b) => MODULE_IDS.indexOf(a) - MODULE_IDS.indexOf(b));
}

export function draftFromPreset(id: PresetDefId): SetupDraft {
  const p = presetById(id);
  return {
    basedOn: id,
    ruleset: p.ruleset,
    humanAssists: p.humanAssists,
    aiProfile: p.aiProfile,
    alignment: null,
    authoredMapJson: null,
  };
}

export const DEFAULT_PRESET: PresetDefId = 'standard';

export function defaultDraft(): SetupDraft {
  return draftFromPreset(DEFAULT_PRESET);
}

function sameModules(a: readonly ModuleId[], b: readonly ModuleId[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((m) => set.has(m));
}

function shallowEqual<T extends object>(a: T, b: T): boolean {
  const ka = Object.keys(a) as (keyof T)[];
  const kb = Object.keys(b) as (keyof T)[];
  return ka.length === kb.length && ka.every((k) => a[k] === b[k]);
}

/** True when the ruleset part (modules, tuning, content, climate, thirst, sea, year length) equals the preset's. */
export function rulesetMatchesPreset(r: Ruleset, p: PresetDef): boolean {
  return (
    r.content === p.ruleset.content &&
    sameModules(r.modules, p.ruleset.modules) &&
    shallowEqual(r.tuning, p.ruleset.tuning) &&
    (r.climate ?? 'temperate') === (p.ruleset.climate ?? 'temperate') &&
    (r.thirst ?? 'auto') === (p.ruleset.thirst ?? 'auto') &&
    (r.sea ?? 'rim') === (p.ruleset.sea ?? 'rim') &&
    (r.yearMinutes ?? DEFAULT_YEAR_MINUTES) === (p.ruleset.yearMinutes ?? DEFAULT_YEAR_MINUTES)
  );
}

/** True when anything (ruleset, assists, AI profile or alignment) differs from `basedOn`. */
export function isCustomised(d: SetupDraft): boolean {
  const p = presetById(d.basedOn);
  return (
    !rulesetMatchesPreset(d.ruleset, p) ||
    !shallowEqual(d.humanAssists, p.humanAssists) ||
    d.aiProfile !== p.aiProfile ||
    d.alignment !== null ||
    d.authoredMapJson !== null
  );
}

/** Re-derive `ruleset.preset` after an edit (keeps modules canonical). */
function normalise(d: SetupDraft): SetupDraft {
  const modules = sortModules(d.ruleset.modules);
  const base: Ruleset = { ...d.ruleset, modules };
  const preset: PresetId = rulesetMatchesPreset(base, presetById(d.basedOn)) ? d.basedOn : 'custom';
  return { ...d, ruleset: { ...base, preset } };
}

/**
 * A draft from a shared ruleset (share code, saved setup). Seats aren't part
 * of the share code, so assists, the AI profile and the alignment default to
 * the base preset's (alignment: null).
 */
export function draftFromRuleset(
  ruleset: Ruleset,
  basedOn: PresetDefId,
  seats?: {
    readonly humanAssists?: Assists;
    readonly aiProfile?: AiProfile;
    readonly alignment?: SeatAlignment | null;
  },
  /** Authored-map JSON carried alongside (not part of the share code). */
  authoredMapJson?: string | null,
): SetupDraft {
  const p = presetById(basedOn);
  return normalise({
    basedOn,
    ruleset,
    humanAssists: seats?.humanAssists ?? p.humanAssists,
    aiProfile: seats?.aiProfile ?? p.aiProfile,
    alignment: seats?.alignment ?? null,
    authoredMapJson: authoredMapJson ?? null,
  });
}

export function resetDraft(d: SetupDraft): SetupDraft {
  return draftFromPreset(d.basedOn);
}

// ── Modules ────────────────────────────────────────────────────────────

/** Mature modules can't be switched on under the family rating. */
export function isModuleLocked(id: ModuleId, content: ContentRating): boolean {
  return content === 'family' && id.startsWith('mature.');
}

export interface ToggleResult {
  readonly draft: SetupDraft;
  /** Modules switched on alongside `id` (its dependencies). */
  readonly added: readonly ModuleId[];
  /** Modules switched off alongside `id` (its dependents). */
  readonly removed: readonly ModuleId[];
}

/**
 * Turn a module on (adding what it needs) or off (removing what needs it).
 * A locked module (mature under family) is left unchanged.
 */
export function setModule(d: SetupDraft, id: ModuleId, on: boolean): ToggleResult {
  const current = new Set(d.ruleset.modules);
  if (on) {
    if (isModuleLocked(id, d.ruleset.content)) return { draft: d, added: [], removed: [] };
    const next = withDependencies([...current, id]);
    // Enabling can't reach a mature module unless it was asked for, and that
    // case is locked above; still run the rating so the set stays valid.
    applyContentRating(next, d.ruleset.content);
    const added = sortModules([...next].filter((m) => !current.has(m) && m !== id));
    return {
      draft: normalise({ ...d, ruleset: { ...d.ruleset, modules: [...next] } }),
      added,
      removed: [],
    };
  }
  const drop = new Set<ModuleId>([id, ...dependents(id)]);
  const removed = sortModules([...drop].filter((m) => m !== id && current.has(m)));
  const next = [...current].filter((m) => !drop.has(m));
  return {
    draft: normalise({ ...d, ruleset: { ...d.ruleset, modules: next } }),
    added: [],
    removed,
  };
}

/** Currently-enabled modules that turning `id` off would also switch off. */
export function enabledDependents(d: SetupDraft, id: ModuleId): ModuleId[] {
  const set = new Set(d.ruleset.modules);
  return sortModules(dependents(id).filter((m) => set.has(m)));
}

/** Modules that turning `id` on would also switch on. */
export function missingDependencies(d: SetupDraft, id: ModuleId): ModuleId[] {
  const set = new Set(d.ruleset.modules);
  return sortModules([...withDependencies([id])].filter((m) => m !== id && !set.has(m)));
}

// ── Tuning, content, seats ─────────────────────────────────────────────

export function setTuning<K extends keyof Tuning>(
  d: SetupDraft,
  key: K,
  value: Tuning[K],
): SetupDraft {
  return normalise({
    ...d,
    ruleset: { ...d.ruleset, tuning: { ...d.ruleset.tuning, [key]: value } },
  });
}

/** Change the content rating; family forces the mature modules off. */
export function setContent(d: SetupDraft, content: ContentRating): SetupDraft {
  const mods = applyContentRating(new Set(d.ruleset.modules), content);
  return normalise({ ...d, ruleset: { ...d.ruleset, content, modules: [...mods] } });
}

export function setAssist<K extends keyof Assists>(
  d: SetupDraft,
  key: K,
  value: Assists[K],
): SetupDraft {
  return normalise({ ...d, humanAssists: { ...d.humanAssists, [key]: value } });
}

export function setAiProfile(d: SetupDraft, aiProfile: AiProfile): SetupDraft {
  return normalise({ ...d, aiProfile });
}

/** Set the map climate (terrain-levels D11). Absent = temperate. */
export function setClimate(
  d: SetupDraft,
  climate: 'arctic' | 'tropical' | 'desert' | 'temperate',
): SetupDraft {
  const { climate: _drop, ...rest } = d.ruleset;
  const ruleset: Ruleset = climate === 'temperate' ? rest : { ...rest, climate };
  return normalise({ ...d, ruleset });
}

/** Set the thirst mode (desert water carrying). Absent = auto: on from AI difficulty level 3. */
export function setThirst(d: SetupDraft, thirst: Thirst): SetupDraft {
  const { thirst: _drop, ...rest } = d.ruleset;
  const ruleset: Ruleset = thirst === 'auto' ? rest : { ...rest, thirst };
  return normalise({ ...d, ruleset });
}

/** Set the sea axis (sea-design §3 A1). Absent = rim: today's coast ring. */
export function setSea(d: SetupDraft, sea: Sea): SetupDraft {
  const { sea: _drop, ...rest } = d.ruleset;
  const ruleset: Ruleset = sea === 'rim' ? rest : { ...rest, sea };
  return normalise({ ...d, ruleset });
}

/**
 * Set the calendar axis (generations design §4a): real minutes per in-game
 * year, 2–12 in half-minute steps. Absent (or the default 6) = the classic
 * pace. Fixed for a match; travels in the full rules, never the share code.
 */
export function setYearMinutes(d: SetupDraft, yearMinutes: number | null): SetupDraft {
  const { yearMinutes: _drop, ...rest } = d.ruleset;
  const ruleset: Ruleset =
    yearMinutes === null || yearMinutes === DEFAULT_YEAR_MINUTES ? rest : { ...rest, yearMinutes };
  return normalise({ ...d, ruleset });
}

/** Set (or clear, with null) seat 0's alignment. */
export function setAlignment(d: SetupDraft, alignment: SeatAlignment | null): SetupDraft {
  return normalise({ ...d, alignment });
}

/** Set (or clear, with null) the authored map to play on. */
export function setAuthoredMap(d: SetupDraft, authoredMapJson: string | null): SetupDraft {
  return { ...d, authoredMapJson };
}

/** Seat 0 is the local human; one AI seat per `tuning.aiOpponents`. */
export function toMatchSetup(d: SetupDraft): MatchSetup {
  const human: Seat =
    d.alignment !== null
      ? { kind: 'human', assists: d.humanAssists, alignment: d.alignment }
      : { kind: 'human', assists: d.humanAssists };
  const seats: Seat[] = [human];
  for (let i = 0; i < d.ruleset.tuning.aiOpponents; i++)
    seats.push({ kind: 'ai', ai: d.aiProfile });
  return {
    ruleset: d.ruleset,
    seats,
    ...(d.authoredMapJson ? { map: d.authoredMapJson } : {}),
  };
}

/** Assists of the local human seat (seat 0), if any. */
export function humanAssistsOf(setup: MatchSetup | null | undefined): Assists | null {
  const seat = setup?.seats[0];
  return seat?.kind === 'human' ? seat.assists : null;
}

/** Midpoint of the preset's session length, rounded to 5 minutes. */
export function estimatedMinutes(d: SetupDraft): number {
  const [lo, hi] = presetById(d.basedOn).sessionMin;
  return Math.round((lo + hi) / 2 / 5) * 5;
}
